import React from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";

const assignRef = (ref, value) => {
    if (!ref) return;
    if (typeof ref === "function") {
        ref(value);
        return;
    }
    if (typeof ref === "object") {
        ref.current = value;
    }
};

const Draggable = React.forwardRef(function Draggable(
    {
        id = "draggable",
        children,
        style: overrideStyle = {},
        data,
        isDraggable = true,
        droppable = true,
        droppableId,
        droppableData,
    },
    forwardedRef
) {
    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id,
        data,
        disabled: !isDraggable,
    });

    const { setNodeRef: setDroppableNodeRef } = useDroppable(
        droppable
            ? {
                  id: droppableId || id,
                  data: droppableData || data,
              }
            : { id, disabled: true }
    );

    const child = React.Children.only(children);
    const mergedRef = (node) => {
        setNodeRef(node);
        if (droppable) {
            setDroppableNodeRef(node);
        }
        assignRef(child.ref, node);
        assignRef(forwardedRef, node);
    };

    const style = {
        ...child.props?.style,
        ...overrideStyle,
        ...(isDraggable ? { cursor: transform ? "grabbing" : "grab" } : {}),
        ...(transform
            ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
            : {}),
    };

    const isEventFromInteractiveElement = (event) => {
        const target = event?.target;
        if (!(target instanceof Element)) return false;
        const interactiveTags = ["INPUT", "TEXTAREA", "SELECT", "OPTION", "BUTTON"];
        return (
            interactiveTags.includes(target.tagName) ||
            target.closest("[data-no-drag]") ||
            target.closest('[contenteditable="true"]')
        );
    };

    const safeListeners = React.useMemo(() => {
        if (!listeners || !isDraggable) return undefined;

        const wrap = (handler) =>
            handler
                ? (event) => {
                      if (isEventFromInteractiveElement(event)) return;
                      handler(event);
                  }
                : undefined;

        return {
            ...listeners,
            onPointerDown: wrap(listeners.onPointerDown),
            onMouseDown: wrap(listeners.onMouseDown),
            onTouchStart: wrap(listeners.onTouchStart),
            onKeyDown: wrap(listeners.onKeyDown),
            onKeyUp: wrap(listeners.onKeyUp),
        };
    }, [listeners]);

    return React.cloneElement(child, {
        ...child.props,
        ...safeListeners,
        ...attributes,
        ref: mergedRef,
        style,
    });
});

export default Draggable;
